// store.ts

import {
  combineReducers,
  configureStore,
  AnyAction,
  Reducer,
} from "@reduxjs/toolkit";

import storage from "redux-persist/lib/storage/session";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

import { encryptTransform } from "redux-persist-transform-encrypt";

/* =========================
   Slice Imports
========================= */

import optionReducer from "./slices/optionSlice";
import tabsReducer from "./slices/tabsSlice";
import dashboardDataReducer from "./slices/dashboardDataSlice";
import authSlice from "./slices/authSlice";
import ccifDataReducer from "./slices/ccifDataSlice";
import selectCcifSlice from "./slices/selectCcifSlice";
import notificationSlice from "./slices/notificationSlice";
import tokenSlice from "./slices/tokenSlice";
import snackbarSlice from "./slices/snackbarSlice";
import mobileEmailSlice from "./slices/mobileEmailSlice";
import accountSummaryDataSlice from "./slices/accountSummarySlice";
import chatSlice from "./slices/chatSlice";
import statusPopupSlice from "./slices/statusPopupSlice";
import disbursementButtonSlice from "./slices/disbursementButtonSlice";
import documentFormSlice from "./slices/documentFormSlice";
import notificationToast from "./slices/toastNotificationSlice";
import paymentUpdateDetailsSlice from "./slices/paymentUpdateDetailsSlice";
import keyRegistrarReducer from "./slices/keyRegistrarSlice";
import stepperReducer from "./slices/stepperDynamicFormSlice";
import loadingReducer from "./slices/loadingSlice";
import applicationDetailsReducer from "./slices/applicationLoanDetailsSlice";
import disbursementRequestReducer from "./slices/disbursementRequestSlice";
import deviceTypeReducer from "./slices/deviceTypeSlice";
import eligibilityCheckSlice from "./slices/newJourneyEligibilitycheckSlice";
import supportTicketReducer from "./slices/supportTicketSlice";
import complaintReducer from "./slices/complaintSlice";

/* =========================
   Shared Encrypt Config
========================= */

const encryptConfig = encryptTransform({
  secretKey: "Sq@r3G@m3-$3cr3t-k3y",
  onError: (err) => console.error("redux encryption error", err),
});

/* =========================
   SLICE LEVEL PERSIST (whitelist)
========================= */

const authPersistConfig = {
  key: "auth",
  storage,
  blacklist: ["statusPopup"],
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: [
    "token",
    "triggerData",
    "authStatus",
    "userProfile",
    "permission",
    "businessType",
    "loanCategory",
    "selectedFormType",
    "requestSignature"
  ],
};

const stepperPersistConfig = {
  key: "stepper",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: [
    "activeVerticalIndex",
    "activeHorizontalIndex",
    "completedHorizontalIndex",
    "formData",
    "trackingId",
    "isMobileVerified",
    "isEmailVerified",
    "isBypassLogin",
    "aadhaarApplicationNo",
    "hasPan",
    "panNumber",
    "isPanVerified",
    "adharMandatory",
    "showAadhaar",
    "lastPanNumber",
    "lastVerifiedPhoneNumber",
    "lastVerifiedEmail",
    "isEligibilityCheckSubmitted",
    "isDocumentVerified",
    "notInList",
    "aadhaarVerifiedDetails",
    "coApplicantVerification",
    "currentCoApplicantIdx",
    "extraCourses",
    "entranceExamEntries",
    "cgpaModes",
    "extraCoApplicant",
    "breStatus",
    "breData",
    "fetchUserApplicationData",
    "isEligible",
    "consentApplicationNo",
    "documentUploadId",
    "aadhaarVerifiedDetailsCoApplicant",
    "isDocumentVerifiedCoApplicant",
    "aadhaarApplicationNoCoApplicant",
    "digiLockerLoaderId",
    "documentApplicantType",
    "triggerData",
    "applicationId",
    "applicantId",
    "checkIsLeadApiInvoked",
    "leadSequenceId",
    "loanDetailCoApplicantCollapseValue",
    "assignedSMIdValue",
    "aadhaarSeedingStatus",
    "applicationSource",
    "disableAddMoreCoApplicant",
    "savedBasicCoApplicants",
    "savedEmploymentCoApplicants",
    "encryptionKey",
    "googleCampKeys"
  ],
};

const chatPersistConfig = {
  key: "chat",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: ["salesUserId", "currentSessionId"],
};
const eligibilityCheckPersistConfig = {
  key: "eligibilityCheck",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: [
    "currentStep",
    "stepFields",
    "trackingId",
  ],
};
// Dashboard tab / product selection survives a reload. The Additional Summary
// completion flags are not persisted: a stale "complete" would let the user
// continue without uploading the required documents.
const tabsPersistConfig = {
  key: "tabs",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  blacklist: [
    "additionalSummaryActiveTab",
    "additionalSummaryDocumentsComplete",
    "additionalSummaryConsentComplete",
  ],
};

/* =========================
   ROOT LEVEL PERSIST (others auto persist)
========================= */

const rootPersistConfig = {
  key: "root",
  storage,

  // handled separately above
  blacklist: ["auth", "stepper", "chat", "loading", "eligibilityCheck", "tabs"],

  transforms: [encryptConfig],
};

/* =========================
   App Reducer
========================= */

const appReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authSlice),
  stepper: persistReducer(stepperPersistConfig, stepperReducer),
  chat: persistReducer(chatPersistConfig, chatSlice),

  eligibilityCheck: persistReducer(
    eligibilityCheckPersistConfig,
    eligibilityCheckSlice,
  ),

  option: optionReducer,
  tabs: persistReducer(tabsPersistConfig, tabsReducer),
  dashboardData: dashboardDataReducer,
  ccifData: ccifDataReducer,
  selectCcif: selectCcifSlice,
  token: tokenSlice,
  mobileEmail: mobileEmailSlice,
  snackbarSlice,
  notification: notificationSlice,
  accountSummaryData: accountSummaryDataSlice,
  statusPopup: statusPopupSlice,
  disbursementButtonSlice,
  documentFormSlice,
  toastNotification: notificationToast,
  paymentUpdateDetailsSlice,
  keyRegistrar: keyRegistrarReducer,
  loading: loadingReducer,
  applicationLoanDetailsData: applicationDetailsReducer,
  disbursementRequestData: disbursementRequestReducer,
  deviceType: deviceTypeReducer,
  supportTicket: supportTicketReducer,
  complaint: complaintReducer,
});

/* =========================
   Root Reducer (logout clear)
========================= */

const rootReducer: Reducer = (state: any, action: AnyAction) => {
  if (action.type === REHYDRATE && action.key === "root" && action.payload?.tabs) {
    action = { ...action, payload: { ...action.payload, tabs: undefined } };
  }
  if (action.type === "USER_LOGOUT") {
    state = undefined; // clears redux memory
  }
  return appReducer(state, action);
};

/* =========================
   Persisted Reducer
========================= */

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

/* =========================
   Store
========================= */

export const store = configureStore({
  reducer: persistedReducer,
  // Redux DevTools exposes the entire live state tree (tokens, user profile,
  // application data) to anyone with the browser extension installed — only
  // wire it up outside production.
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

/* =========================
   Types
========================= */

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
